import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, ExternalLink } from 'lucide-react';
import { authService } from '@/services/auth-service';

export default function PublicHeader() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
   const location = useLocation();
   const navigate = useNavigate();

  const handleLoginClick = async () => {
    // On non-home public pages, go to homepage
    if (location.pathname !== '/') {
      navigate('/');
      setMenuOpen(false);
      return;
    }

    // On homepage, start Entra login (same as "Sätt igång")
    try {
      const { authorizationUrl } = await authService.getOAuthUrl('microsoft', { prompt: 'login' });
      window.location.href = authorizationUrl;
    } catch (err) {
      console.error('Failed to initiate Entra B2C login from header:', err);
      alert('Kunde inte starta inloggning. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setMenuOpen(false);
    }
  };

  return (
    <header className="border-b border-border">
      <div className="w-full flex items-center justify-between px-4 py-4">
        <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
          <img src="/icons/logo.svg" alt="App logo" className="h-8 w-auto" />
          <span className="text-2xl font-bold neon-glow leading-8">{t('app.name')}</span>
        </Link>

        {/* Hamburger menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 rounded-md transition-colors hover:bg-[#67724e]/20 flex items-center"
            aria-label="Meny"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Dropdown meny */}
          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-popover border z-50">
                <div className="py-1">
                  <button
                    type="button"
                    onClick={handleLoginClick}
                    className="w-full text-left px-4 py-2 text-sm font-medium transition-colors hover:bg-[#67724e]/20"
                  >
                    Logga in / Skapa konto
                  </button>
                  <div className="border-t border-border my-1" />
                  <a
                    href="https://blogg.beredskapshubben.se/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Blogg
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  </a>
                  <a
                    href="/about"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Om oss
                  </a>
                  <a
                    href="/pricing"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Prisplaner
                  </a>
                  <div className="border-t border-border my-1" />
                  <a
                    href="/terms"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Användarvillkor
                  </a>
                  <a
                    href="/privacy"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Integritetspolicy
                  </a>
                  <a
                    href="/cookies"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Cookiepolicy
                  </a>
                  <a
                    href="/community-guidelines"
                    className="block px-4 py-2 text-sm transition-colors hover:bg-[#67724e]/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Communityregler
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
